import { useEffect, useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CalendarDays, ChevronDown, Coffee, LogOut, RotateCcw } from 'lucide-react';
import Popover from '../ui/Popover';
import Menu, { type MenuItemDef } from '../ui/Menu';
import { ClockInChoice } from './ClockInChoice';
import { useAttendancePanel } from '../../hooks/workspace';
import { usePermissions } from '../../hooks/usePermissions';
import { toast } from '../../store/toastStore';
import { apiErrorMessage } from '../sales/shared';
import { appLocale } from '../../utils/format';
import { formatClock, formatHoursMinutes, type AttendanceState } from '../home/homeModel';
import { needsClockIn } from '../documentation/documentationModel';
import { requestClockOut as requestClockOutFlow } from './attendanceFlow';
import { useBreakElapsed, useWorkedMinutes } from './useWorkedMinutes';

const ICON = { size: 16, strokeWidth: 1.75 } as const;

/** State dot: live (brand) on shift, warning on break. */
function AttendanceDot({ state }: { state: AttendanceState }) {
  const cls = state === 'clocked_in' ? 'bg-live animate-live' : 'bg-warning-600';
  return <span className={`inline-block size-2 flex-shrink-0 rounded-full ${cls}`} aria-hidden="true" />;
}


/**
 * The one attendance control (owner, 2026-10-11): in the top bar on every
 * screen, for the roles that clock in (admin, agent, as `useAttendancePanel`).
 * It replaces the Home header's clock-in / "סיום יום", the dashboard's side
 * clock, the QuickCreate item and the avatar-menu item.
 *
 * - Not on shift: a quiet secondary "החתמת כניסה" that opens משרד · בית
 *   (`ClockInChoice`).
 * - On shift: "● במשמרת 3:12 ▾" (time worked today); on a break "● בהפסקה ▾".
 *   A press opens a menu: break / back from break, "החתמת יציאה" (a delayed
 *   commit with "ביטול", B.23), and "הנוכחות שלי".
 * - Phone: the dot and the time only, on a 44 px target.
 * - Every outcome is a toast, which the LiveAnnouncer also reads; a failure
 *   without a server message gets a default text.
 */
export default function AttendanceControl() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { canPath } = usePermissions();
  const attendance = useAttendancePanel();
  const locale = appLocale(i18n.language);
  const [open, setOpen] = useState(false);
  const [checking, setChecking] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuId = useId();
  const headingId = useId();

  const state: AttendanceState = attendance.status?.status ?? 'clocked_out';
  const worked = useWorkedMinutes(state, attendance.status?.today_minutes ?? 0, attendance.updatedAt);
  const workedText = formatHoursMinutes(worked);
  const breakElapsed = useBreakElapsed(state === 'on_break', attendance.status?.since);

  // Clocking in or out swaps the trigger (choice ↔ menu button). When the swap
  // follows the user's own action and focus fell to the page, focus the new
  // trigger, so a keyboard user stays on the control.
  const wrapRef = useRef<HTMLDivElement>(null);
  const keepFocus = useRef(false);
  useEffect(() => {
    if (!keepFocus.current) return;
    const active = document.activeElement;
    if (active && active !== document.body && wrapRef.current?.contains(active)) { keepFocus.current = false; return; }
    if (active && active !== document.body) return;
    keepFocus.current = false;
    wrapRef.current?.querySelector<HTMLElement>('button')?.focus({ preventScroll: true });
  }, [state]);

  /**
   * "החתמת יציאה": P-10e (D-85) — the attendance flow host decides: "סגירת
   * יום" (with the running timers on top) when a timer runs, or something is
   * left to document (suggestions, or 15+ minutes of attendance without work
   * logs); otherwise the delayed clock-out with "ביטול", exactly as before.
   */
  const requestClockOut = async () => {
    keepFocus.current = true;
    setChecking(true);
    try {
      await requestClockOutFlow();
    } catch (err) {
      toast('error', apiErrorMessage(err, t('attendance.clockOutFailed')));
    } finally {
      setChecking(false);
    }
  };

  if (!attendance.isAgentOrAdmin) return null;
  if (attendance.isLoading) {
    return <span className="block h-control-sm w-11 rounded-full bg-surface-subtle animate-pulse md:w-28" aria-hidden="true" />;
  }
  // The status did not load: show nothing rather than a clock-in that may be wrong.
  if (!attendance.status) return null;

  const failed = (key: string) => (err: unknown) => toast('error', apiErrorMessage(err, t(key)));
  const timeOf = (iso: unknown) => formatClock(typeof iso === 'string' ? iso : new Date().toISOString(), locale);

  // D-116 — clocked out today: no second clock-in (one per day), but
  // "חזרה לעבודה" — the time away becomes a break and the day is open again.
  if (state === 'clocked_out' && attendance.status.can_return_to_work) {
    return (
      <div ref={wrapRef} className="contents">
        <button
          type="button"
          className="btn btn-secondary h-control-sm gap-1.5 px-3"
          disabled={attendance.isPending}
          aria-busy={attendance.isPending || undefined}
          onClick={() => {
            keepFocus.current = true;
            attendance.backToWork(undefined, {
              onSuccess: () => toast('success', t('attendance.backToWorkDone')),
              onError: failed('attendance.backToWorkFailed'),
            });
          }}
        >
          <RotateCcw {...ICON} aria-hidden="true" />
          <span>{t('attendance.backToWork')}</span>
        </button>
      </div>
    );
  }

  if (state === 'clocked_out') {
    // Not clocked in yet today (and no clock-out either): the control stands
    // out in the brand fill until the day starts (documentation brief, part 1).
    // After a clock-out it is the quiet secondary again.
    const emphasis = needsClockIn(attendance.status) ? 'btn-primary' : 'btn-secondary';
    return (
      <div ref={wrapRef} className="contents">
        <ClockInChoice
          buttonClassName={`btn ${emphasis} h-control-sm gap-1.5 px-3`}
          pending={attendance.isPending}
          align="end"
          onOpen={() => attendance.setError('')}
          onPick={(loc) => {
            keepFocus.current = true;
            attendance.clockIn(loc, {
              onSuccess: (res) => toast('success', t('home.attendance.clockedInAt', { time: timeOf(res?.data?.recorded_at) })),
              onError: failed('attendance.clockInFailed'),
            });
          }}
        />
      </div>
    );
  }

  const onBreak = state === 'on_break';
  const dismiss = (refocus: boolean) => {
    setOpen(false);
    if (refocus) triggerRef.current?.focus({ preventScroll: true });
  };

  const items: MenuItemDef[] = [
    onBreak
      ? {
        key: 'break-end',
        label: t('home.attendance.endBreak'),
        icon: <Coffee {...ICON} />,
        disabled: attendance.isPending,
        onSelect: () => attendance.breakEnd(undefined, {
          onSuccess: () => toast('success', t('home.attendance.breakEnded')),
          onError: failed('attendance.breakEndFailed'),
        }),
        data: { 'data-attendance-action': 'break-end' },
      }
      : {
        key: 'break-start',
        label: t('attendance.breakStart'),
        icon: <Coffee {...ICON} />,
        disabled: attendance.isPending,
        // P-10e (D-85) — the break window when timers run (the host reports the outcome).
        onSelect: () => attendance.breakStart(undefined, { onError: failed('attendance.breakStartFailed') }),
        data: { 'data-attendance-action': 'break-start' },
      },
    {
      key: 'clock-out',
      label: t('home.attendance.endDay'),
      icon: <LogOut {...ICON} className="rtl-mirror" />,
      disabled: attendance.isPending || checking,
      onSelect: () => { void requestClockOut(); },
      data: { 'data-attendance-action': 'clock-out' },
    },
    ...(canPath('/attendance') ? [{
      key: 'page',
      label: t('attendance.myAttendance'),
      icon: <CalendarDays {...ICON} />,
      separatorBefore: true,
      onSelect: () => navigate('/attendance'),
    } satisfies MenuItemDef] : []),
  ];

  const stateLabel = onBreak ? t('home.status.onBreak') : t('home.status.onShift');
  const triggerLabel = onBreak
    ? t('home.attendance.triggerOnBreakTimed', { time: breakElapsed })
    : t('home.attendance.triggerOnShift', { time: workedText });

  return (
    <div ref={wrapRef} className="contents">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => { if (e.key === 'ArrowDown' && !open) { e.preventDefault(); setOpen(true); } }}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={triggerLabel}
        data-tooltip-owner=""
        data-attendance-control={state}
        className={`inline-flex h-11 min-w-11 flex-shrink-0 items-center justify-center gap-2 rounded-full px-2.5 text-sm font-medium transition-colors duration-micro md:h-control-sm md:min-w-0 md:border md:ps-3 md:pe-2 ${
          onBreak
            ? 'bg-warning-100 text-warning-700 hover:bg-warning-200 md:border-warning-600'
            : 'text-text hover:bg-surface-subtle md:border-border md:bg-surface'
        }`}
      >
        <AttendanceDot state={state} />
        <span className="hidden md:inline">{stateLabel}</span>
        {onBreak && <><span className="hidden md:inline" aria-hidden="true">·</span><bdi dir="ltr" className="tabular-nums" data-break-elapsed="">{breakElapsed}</bdi></>}
        {!onBreak && <bdi dir="ltr" className="tabular-nums">{workedText}</bdi>}
        {/* C-10 (clarity, D-09): "· יציאה" names the menu's main item on desktop; a phone keeps "● 5:40". */}
        {!onBreak && <span className="hidden text-text-2 md:inline">· {t('home.attendance.triggerExit')}</span>}
        <ChevronDown size={14} strokeWidth={1.75} className="hidden text-text-2 md:block" aria-hidden="true" />
      </button>
      <Popover
        open={open}
        onClose={(reason) => dismiss(reason !== 'outside')}
        anchorRef={triggerRef}
        align="end"
        className="w-64 p-1.5"
      >
        <div id={headingId} className="flex items-center gap-2.5 px-2.5 pb-2 pt-1.5">
          <AttendanceDot state={state} />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-text">{stateLabel}</p>
            <p className="text-xs tabular-nums text-text-2">{t('shell.attendance.today', { time: workedText })}</p>
          </div>
        </div>
        <div className="mx-2 mb-1 h-px bg-border" aria-hidden="true" />
        <Menu id={menuId} items={items} labelledBy={headingId} onDismiss={() => dismiss(true)} />
      </Popover>
    </div>
  );
}
